import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { API_BASE_URL, apiRequest, broadcastAdminUpdate } from '../../utils/api';
import { getModulePermissions } from '../../utils/permissions';
import AccessDenied from '../AccessDenied';

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  username: '',
  roleId: '',
  photo: '',
  password: '',
  confirmPassword: '',
};

const sanitizePhone = (value) => (value || '').replace(/[^0-9]/g, '');

const resolvePhotoUrl = (photo) => {
  if (!photo) {
    return '';
  }

  if (photo.startsWith('http://') || photo.startsWith('https://') || photo.startsWith('data:') || photo.startsWith('blob:')) {
    return photo;
  }

  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const normalizedPath = photo.startsWith('/') ? photo : `/${photo}`;
  return `${baseUrl}${normalizedPath}`;
};

const UserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [alert, setAlert] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [photoName, setPhotoName] = useState('');
  const [currentPhoto, setCurrentPhoto] = useState('');
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');

  const permissions = useMemo(() => getModulePermissions('settings-user'), []);
  const canEdit = permissions.edit;

  const validateField = useCallback((field, value, allValues = formData) => {
    switch (field) {
      case 'name': {
        const trimmed = (value || '').trim();
        if (!trimmed) return 'Name is required.';
        if (trimmed.length < 2) return 'Name must be at least 2 characters.';
        return '';
      }
      case 'email': {
        const trimmed = (value || '').trim();
        if (!trimmed) return 'Email is required.';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) return 'Enter a valid email address.';
        return '';
      }
      case 'phone': {
        const digits = sanitizePhone(value);
        if (!digits) return 'Phone number is required.';
        if (digits.length < 10 || digits.length > 15) return 'Phone number must have between 10 and 15 digits.';
        return '';
      }
      case 'username': {
        const trimmed = (value || '').trim();
        if (!trimmed) return 'Username is required.';
        if (trimmed.length < 3) return 'Username must be at least 3 characters.';
        if (!/^[a-zA-Z0-9._-]+$/.test(trimmed))
          return 'Username can contain letters, numbers, dots, underscores and hyphens only.';
        return '';
      }
      case 'roleId':
        return value ? '' : 'Please select a role.';
      case 'password': {
        if (!value) return '';
        if (value.length < 6) return 'Password must be at least 6 characters.';
        return '';
      }
      case 'confirmPassword': {
        if (!allValues.password && !value) return '';
        if (value !== allValues.password) return 'Passwords do not match.';
        return '';
      }
      default:
        return '';
    }
  }, [formData]);

  const validateForm = (values) => {
    const fieldsToCheck = ['name', 'email', 'phone', 'username', 'roleId'];
    const validationErrors = {};

    fieldsToCheck.forEach((field) => {
      const error = validateField(field, values[field], values);
      if (error) {
        validationErrors[field] = error;
      }
    });

    if (values.password || values.confirmPassword) {
      const passwordError = validateField('password', values.password, values);
      if (passwordError) {
        validationErrors.password = passwordError;
      }
      const confirmError = validateField('confirmPassword', values.confirmPassword, values);
      if (confirmError) {
        validationErrors.confirmPassword = confirmError;
      }
    }

    return validationErrors;
  };

  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const response = await apiRequest('/api/admin/roles', {}, navigate);
      setRoles(response.data || []);
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to load roles.' });
    } finally {
      setLoadingRoles(false);
    }
  }, [navigate]);

  const fetchUser = useCallback(async () => {
    setLoadingUser(true);
    setAlert({ type: '', text: '' });
    setErrors({});

    try {
      const response = await apiRequest(`/api/admin/settings/users/${id}`, {}, navigate);
      const user = response.data || {};

      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || '',
        roleId: user.roleId
          ? `${user.roleId}`
          : user.role?.id
            ? `${user.role.id}`
            : '',
        photo: user.photo || '',
        password: '',
        confirmPassword: '',
      });
      setCurrentPhoto(user.photo || '');
      setPhotoName('');
      setSelectedPhotoFile(null);
      setPhotoPreviewUrl('');
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to load user details.' });
    } finally {
      setLoadingUser(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!canEdit) return;
    fetchRoles();
    fetchUser();
  }, [canEdit, fetchRoles, fetchUser]);

  useEffect(() => {
    if (!formData.roleId || roles.length === 0) return;
    const exists = roles.some((item) => `${item.id}` === `${formData.roleId}`);
    if (!exists) {
      setFormData((prev) => ({ ...prev, roleId: '' }));
    }
  }, [roles, formData.roleId]);

  const handleInputChange = (field) => (event) => {
    let { value } = event.target;
    if (field === 'phone') {
      value = sanitizePhone(value);
    }

    const updated = { ...formData, [field]: value };
    setFormData(updated);

    setErrors((prev) => {
      const newErrors = { ...prev };
      const fieldError = validateField(field, value, updated);
      if (fieldError) {
        newErrors[field] = fieldError;
      } else {
        delete newErrors[field];
      }

      if (field === 'password') {
        const confirmError = validateField('confirmPassword', updated.confirmPassword, updated);
        if (confirmError) {
          newErrors.confirmPassword = confirmError;
        } else {
          delete newErrors.confirmPassword;
        }
      }

      return newErrors;
    });
  };

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
    };
  }, [photoPreviewUrl]);

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];

    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
      setPhotoPreviewUrl('');
    }

    if (!file) {
      setPhotoName('');
      setSelectedPhotoFile(null);
      setErrors((prev) => {
        const { photo, ...rest } = prev;
        return rest;
      });
      return;
    }

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setErrors((prev) => ({ ...prev, photo: 'Photo must be a JPG, PNG, or WEBP image.' }));
      setPhotoName('');
      setSelectedPhotoFile(null);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, photo: 'Photo must be smaller than 2MB.' }));
      setPhotoName('');
      setSelectedPhotoFile(null);
      return;
    }

    setSelectedPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setPhotoName(file.name);
    setErrors((prev) => {
      const { photo, ...rest } = prev;
      return rest;
    });
  };

  const displayPhoto = photoPreviewUrl || currentPhoto || formData.photo;
  const previewPhoto = useMemo(() => resolvePhotoUrl(displayPhoto), [displayPhoto]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canEdit) {
      setAlert({ type: 'error', text: 'You do not have permission to edit users.' });
      return;
    }

    const trimmedValues = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      username: formData.username.trim(),
    };

    const validationErrors = validateForm(trimmedValues);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setAlert({ type: 'error', text: 'Please fix the highlighted fields.' });
      return;
    }

    setSubmitting(true);
    setAlert({ type: '', text: '' });

    const payload = new FormData();
    payload.append('name', trimmedValues.name);
    payload.append('email', trimmedValues.email.toLowerCase());
    payload.append('phone', sanitizePhone(trimmedValues.phone));
    payload.append('username', trimmedValues.username);
    payload.append('roleId', trimmedValues.roleId);

    if (selectedPhotoFile) {
      payload.append('photo', selectedPhotoFile);
    }

    if (formData.password || formData.confirmPassword) {
      payload.append('password', formData.password);
      payload.append('confirmPassword', formData.confirmPassword);
    }

    try {
      const response = await apiRequest(
        `/api/admin/settings/users/${id}`,
        { method: 'PUT', body: payload },
        navigate
      );

      const updatedUser = response.data || {};
      setAlert({ type: 'success', text: response.message || 'User updated successfully.' });
      setErrors({});
      setFormData((prev) => ({
        ...prev,
        password: '',
        confirmPassword: '',
        photo: updatedUser.photo || prev.photo,
      }));
      if (photoPreviewUrl) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
      setPhotoPreviewUrl('');
      setSelectedPhotoFile(null);
      setCurrentPhoto(updatedUser.photo || currentPhoto);
      setPhotoName('');

      try {
        const storedAdmin = JSON.parse(localStorage.getItem('authAdmin') || 'null');
        if (storedAdmin?.id === updatedUser.id) {
          const mergedAdmin = {
            ...storedAdmin,
            ...updatedUser,
            role: updatedUser.role || storedAdmin.role,
            menus: storedAdmin.menus || [],
          };
          localStorage.setItem('authAdmin', JSON.stringify(mergedAdmin));
          broadcastAdminUpdate(mergedAdmin);
        }
      } catch (err) {
        console.error('Failed to update cached admin profile:', err);
      }
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to update user.' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderAlert = () => {
    if (!alert.text) return null;
    const alertClass = alert.type === 'success' ? 'alert-success' : 'alert-danger';
    return (
      <div className={`alert ${alertClass}`} role="alert">
        {alert.text}
      </div>
    );
  };

  if (!canEdit) {
    return <AccessDenied />;
  }

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Edit User</h4>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/settings/users')}
              type="button"
            >
              Back
            </button>
          </div>
        </div>

        {renderAlert()}

        <div className="card">
          <div className="card-body">
            <h5 className="card-title mb-4">User Details</h5>

            {loadingUser ? (
              <div className="text-center py-4">Loading user details...</div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        id="name"
                        placeholder="Enter Name"
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        disabled={submitting}
                      />
                      <label htmlFor="name">Enter Name *</label>
                    </div>
                    {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        id="email"
                        placeholder="Enter Mail ID"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        disabled={submitting}
                      />
                      <label htmlFor="email">Enter Mail ID *</label>
                    </div>
                    {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="tel"
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        id="phone"
                        placeholder="Enter Phone Number"
                        value={formData.phone}
                        onChange={handleInputChange('phone')}
                        disabled={submitting}
                      />
                      <label htmlFor="phone">Enter Phone Number *</label>
                    </div>
                    {errors.phone && <div className="invalid-feedback d-block">{errors.phone}</div>}
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                        id="username"
                        placeholder="Enter Username"
                        value={formData.username}
                        onChange={handleInputChange('username')}
                        disabled={submitting}
                      />
                      <label htmlFor="username">Enter Username *</label>
                    </div>
                    {errors.username && <div className="invalid-feedback d-block">{errors.username}</div>}
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <select
                        className={`form-select ${errors.roleId ? 'is-invalid' : ''}`}
                        id="role"
                        value={formData.roleId}
                        onChange={handleInputChange('roleId')}
                        disabled={loadingRoles || roles.length === 0 || submitting}
                      >
                        <option value="">{loadingRoles ? 'Loading roles...' : 'Select Role'}</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name || role.description || `Role #${role.id}`}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="role">Select Role *</label>
                    </div>
                    {errors.roleId && <div className="invalid-feedback d-block">{errors.roleId}</div>}
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="file"
                        className={`form-control ${errors.photo ? 'is-invalid' : ''}`}
                        id="photo"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        disabled={submitting}
                      />
                      <label htmlFor="photo">Upload Photo</label>
                    </div>
                    {photoName && !errors.photo && <div className="form-text">Selected: {photoName}</div>}
                    {errors.photo && <div className="invalid-feedback d-block">{errors.photo}</div>}
                  </div>

                  {previewPhoto && (
                    <div className="col-md-4">
                      <div className="d-flex flex-column">
                        <span className="small text-muted mb-2">Profile Photo Preview</span>
                        <img
                          src={previewPhoto}
                          alt="Profile preview"
                          className="rounded"
                          style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="password"
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        id="password"
                        placeholder="Create a Password"
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        disabled={submitting}
                      />
                      <label htmlFor="password">New Password</label>
                    </div>
                    {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="password"
                        className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                        id="confirmPassword"
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange('confirmPassword')}
                        disabled={submitting}
                      />
                      <label htmlFor="confirmPassword">Confirm Password</label>
                    </div>
                    {errors.confirmPassword && (
                      <div className="invalid-feedback d-block">{errors.confirmPassword}</div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <button type="submit" className="btn btn-danger" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserEdit;
